/**
 * 체크아웃 검증 유틸리티
 *
 * @see #131 - CheckoutPage 검증 로직 함수화
 */

import { PHONE_REGEX, ORDER_ITEM_LIMITS } from '@/constants/validation';
import type { CartItem } from '@/types';
import type { OrderItem, OrderItemOption } from '@/lib/api/types';

/**
 * 검증 에러 인터페이스
 */
export interface ValidationError {
  field: string;
  message: string;
}

/**
 * 체크아웃 폼 데이터 인터페이스
 */
export interface CheckoutFormData {
  name: string;
  phone: string;
  address: string;
}

/**
 * 체크아웃 번역 인터페이스 (필요한 키만)
 */
export interface CheckoutValidationTranslations {
  pleaseEnterName: string;
  pleaseEnterPhone: string;
  pleaseEnterAddress: string;
  invalidPhoneFormat?: string;
  invalidStore?: string;
  multipleStoresNotSupported?: string;
  invalidMenuItem?: string;
  cartEmpty?: string;
  invalidQuantity?: string;
  instructionsTooLong?: string;
}

/**
 * 체크아웃 폼 검증
 *
 * @param formData 폼 데이터
 * @param orderType 주문 타입
 * @param t 번역 객체
 * @returns 첫 번째 검증 에러 또는 null
 */
export function validateCheckoutForm(
  formData: CheckoutFormData,
  orderType: 'pickup' | 'delivery' | 'table',
  t: CheckoutValidationTranslations
): ValidationError | null {
  const { name, phone, address } = formData;

  // #628 F-03: 테이블 주문은 QR 로 신원이 식별되고 결제가 식후 카운터이므로 이름/전화를 강제하지
  // 않는다(선택 입력). 픽업/배송은 식별·연락이 필요하므로 기존대로 필수.
  const identityOptional = orderType === 'table';

  // 이름 검증 (테이블은 선택)
  if (!identityOptional && !name.trim()) {
    return { field: 'name', message: t.pleaseEnterName };
  }

  // 전화번호 검증 (테이블은 선택)
  if (!identityOptional && !phone.trim()) {
    return { field: 'phone', message: t.pleaseEnterPhone };
  }

  // 전화번호 형식 검증 — 입력된 경우에만(선택 입력 허용). 테이블 빈 값은 통과.
  if (phone.trim() && !PHONE_REGEX.test(phone.trim())) {
    return {
      field: 'phone',
      message: t.invalidPhoneFormat || 'Please enter a valid phone number',
    };
  }

  // 배송 주소 검증 (배송 주문인 경우)
  if (orderType === 'delivery' && !address.trim()) {
    return { field: 'address', message: t.pleaseEnterAddress };
  }

  return null;
}

/**
 * 매장 ID 검증 결과
 */
export interface StoreValidationResult {
  success: boolean;
  storeId?: number;
  error?: string;
}

/**
 * 장바구니 아이템의 매장 ID를 검증
 *
 * @param cartItems 장바구니 아이템
 * @param t 번역 객체
 * @returns 검증 결과
 */
export function validateStoreId(
  cartItems: CartItem[],
  t: CheckoutValidationTranslations
): StoreValidationResult {
  if (cartItems.length === 0) {
    return { success: false, error: t.cartEmpty || 'Your cart is empty' };
  }

  const firstItem = cartItems[0];
  const storeId = parseInt(firstItem.restaurantId, 10);

  if (!storeId || storeId <= 0) {
    return { success: false, error: t.invalidStore || 'Invalid store' };
  }

  // 다중 매장 검증
  const hasMultipleStores = cartItems.some(
    (item) => parseInt(item.restaurantId, 10) !== storeId
  );

  if (hasMultipleStores) {
    return {
      success: false,
      error: t.multipleStoresNotSupported || 'Cannot order from multiple stores at once',
    };
  }

  return { success: true, storeId };
}

/**
 * 주문 아이템 변환 결과
 */
export interface OrderItemsResult {
  success: boolean;
  items?: OrderItem[];
  error?: string;
}

/**
 * CartItem의 selectedOptions를 API OrderItemOption[] 형식으로 변환
 *
 * @param selectedOptions 선택된 옵션 (그룹ID → 옵션ID[])
 * @returns API 형식의 옵션 배열
 *
 * @example
 * // 입력: { "1": ["10", "11"], "2": ["20"] }
 * // 출력: [{ option_id: 1, choice_ids: [10, 11] }, { option_id: 2, choice_ids: [20] }]
 */
function convertToApiOptions(
  selectedOptions?: Record<string, string[]>,
  inputValues?: Record<string, string>,
): OrderItemOption[] | undefined {
  const hasSelections = selectedOptions && Object.keys(selectedOptions).length > 0;
  const hasInputs = inputValues && Object.keys(inputValues).length > 0;
  if (!hasSelections && !hasInputs) {
    return undefined;
  }

  const apiOptions: OrderItemOption[] = [];

  // 선택형 옵션 (single/multiple)
  if (selectedOptions) {
    for (const [groupId, optionIds] of Object.entries(selectedOptions)) {
      const optionId = parseInt(groupId, 10);
      if (isNaN(optionId)) continue;

      const choiceIds = optionIds
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));

      if (choiceIds.length === 0) continue;

      apiOptions.push({
        option_id: optionId,
        choice_ids: choiceIds,
      });
    }
  }

  // #498: 입력형 옵션 (number_input/text_input/textarea) BE 전송.
  // OrderItemOption.input_value 필드에 사용자 입력값 매핑.
  if (inputValues) {
    for (const [groupId, value] of Object.entries(inputValues)) {
      const trimmed = value.trim();
      if (!trimmed) continue;

      const optionId = parseInt(groupId, 10);
      if (isNaN(optionId)) continue;

      apiOptions.push({
        option_id: optionId,
        choice_ids: [],
        input_value: trimmed,
      });
    }
  }

  return apiOptions.length > 0 ? apiOptions : undefined;
}

/**
 * 장바구니 아이템을 주문 아이템으로 변환 및 검증
 *
 * selectedOptions를 API가 기대하는 options 형식으로 변환합니다:
 * - 프론트엔드: { "그룹ID": ["선택ID1", "선택ID2"] }
 * - API: [{ option_id: 그룹ID, choice_ids: [선택ID1, 선택ID2] }]
 *
 * @param cartItems 장바구니 아이템
 * @param t 번역 객체
 * @returns 변환 결과
 */
export function convertAndValidateOrderItems(
  cartItems: CartItem[],
  t: CheckoutValidationTranslations
): OrderItemsResult {
  const orderItems: OrderItem[] = [];

  for (const item of cartItems) {
    const menuIdStr = item.menuItemId || item.id;
    const menuId = parseInt(menuIdStr, 10);

    // menu_id 검증
    if (isNaN(menuId) || menuId <= 0) {
      return {
        success: false,
        error: t.invalidMenuItem || `Invalid menu item: ${menuIdStr}`,
      };
    }

    // quantity 검증
    if (item.quantity <= 0 || item.quantity > ORDER_ITEM_LIMITS.MAX_QUANTITY) {
      return { success: false, error: t.invalidQuantity || 'Invalid quantity' };
    }

    // special_instructions 길이 제한
    const instructions = item.customizations?.join(', ') || '';
    if (instructions.length > ORDER_ITEM_LIMITS.MAX_INSTRUCTIONS_LENGTH) {
      return { success: false, error: t.instructionsTooLong || 'Instructions are too long' };
    }

    // 선택된 옵션 + 입력형 옵션을 API 형식으로 변환 (#498)
    const options = convertToApiOptions(item.selectedOptions, item.inputValues);

    orderItems.push({
      menu_id: menuId,
      quantity: item.quantity,
      options,
      special_instructions: instructions || undefined,
    });
  }

  return { success: true, items: orderItems };
}
